import React, { useState, useEffect } from 'react'
import { styled } from '@mui/material'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'

// Components
import StyledTable from '../../../components/ReactTable/StyledTable'
import StyledTableContainer from '../../../components/StyledTableContainer/StyledTableContainer'
import StyledEmptyHeader from '../../../components/StyledEmptyHeader/StyledEmptyHeader'
import AddMttrMtbfFilterModal from './MttrMtbfAddFilterModal'
import { nanoid } from 'nanoid'
import StyledSelectField from '../../../components/StyledSelectField/StyledSelectField'

// Services
import filterConfigService from '../../../service/filterConfig.service'

// Utils
import { showNotification } from '../../../utils/toast.utils'

const FilterContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-between',
  alignItems: 'center',
  columnGap: '16px'
}))

const parameterOptions = [
  { value: 'MTTR', label: 'MTTR' },
  { value: 'MTBF', label: 'MTBF' }
]

const MttrMtbfFilters = () => {
  const defaultParameter = { value: 'all', label: 'All parameters' }

  const [isLoading, setIsLoading] = useState(false)
  const [filterData, setFilterData] = useState([])
  const [openUpdateModal, setOpenUpdateModal] = useState(false)
  const [selectedParameter, setSelectedParameter] = useState(defaultParameter)

  const fetchFilterData = async () => {
    try {
      setIsLoading(true)
      const response = await filterConfigService.getFilterConfigs()
      console.log('Full API Response:', response.data)

      if (response.data?.success && Array.isArray(response.data.data)) {
        const formattedData = response.data.data.flatMap((item) => {
          if (!Array.isArray(item.filterData)) {
            console.error('Invalid filterData format:', item.filterData)
            return []
          }

          return item.filterData.map((filterOption) => ({
            id: nanoid(),
            parameter: item.parameter,
            filterOption
          }))
        })

        setFilterData(formattedData)
      } else {
        console.log('Invalid API response:', response.data)
        setFilterData([])
      }
    } catch (err) {
      console.log('Error fetching data:', err)
      setFilterData([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchFilterData()
  }, [])

  const updateMttrMtbfFilter = async (data) => {
    try {
      setIsLoading(true)
      const response = await filterConfigService.addFilterConfig(data)
      if (response.data.success) {
        showNotification(`Parameter options added successfully`, 'success')
        setOpenUpdateModal(false)
      } else {
        showNotification(response.data.message, 'error')
      }
      fetchFilterData()
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }

  const filterColumns = [
    {
      Header: 'S.No',
      accessor: 'index',
      Cell: (rowData) => {
        return rowData.row.index + 1
      }
    },
    { Header: 'Parameter', accessor: 'parameter' },
    { Header: 'Filter Options (in mins)', accessor: 'filterOption' }
  ]

  let dataToDisplay = [...filterData]

  if (selectedParameter.value !== 'all') {
    dataToDisplay = dataToDisplay.filter(
      (item) => item.parameter === selectedParameter.value
    )
  }

  return (
    <>
      <Stack
        sx={{
          position: 'sticky',
          top: 0,
          width: '100%',
          flexDirection: 'column',
          borderBottom: '1px solid #eeeeee',
          backgroundColor: '#fff',
          zIndex: 100
        }}
      >
        <Stack sx={{ flexDirection: 'row', gap: '16px', padding: '12px 16px' }}>
          <Button
            variant="contained"
            sx={{ textTransform: 'none', color: '#fff' }}
            onClick={() => {
              setOpenUpdateModal(true)
            }}
          >
            Update filter
          </Button>
          <Stack sx={{ width: '200px' }}>
            <StyledSelectField
              label=""
              placeHolderText="Select parameter"
              selectedValue={selectedParameter}
              selectOptions={[defaultParameter, ...parameterOptions]}
              onChangeHandler={(value) => setSelectedParameter(value)}
              isMulti={false}
              isDisabled={false}
            />
          </Stack>
        </Stack>
      </Stack>

      <Box sx={{ padding: '16px' }}>
        <StyledTableContainer>
          {isLoading && <StyledEmptyHeader>Loading...</StyledEmptyHeader>}
          {!isLoading && filterData.length === 0 && (
            <StyledEmptyHeader>There are no data</StyledEmptyHeader>
          )}
          {!isLoading && filterData.length > 0 && (
            <StyledTable
              pagination={false}
              columns={filterColumns}
              data={dataToDisplay}
              hiddenColumns={['id']}
            />
          )}
        </StyledTableContainer>
      </Box>

      {openUpdateModal && (
        <AddMttrMtbfFilterModal
          isModalOpen={openUpdateModal}
          closeHandler={() => setOpenUpdateModal(false)}
          primaryAction={updateMttrMtbfFilter}
          defaultData={filterData}
        />
      )}
    </>
  )
}

export default MttrMtbfFilters
